import { useCallback, useState } from 'react';

import { formatCompact, type Sample } from '../../model';
import { ChartFrame, ChartNoticeRow, type ChartLine } from '../ChartFrame';
import { useLab } from '../LabContext';
import { InstantThreatParams } from '../ScenarioPanel';
import { Field, NumberField, ScaleToggle } from '../controls';
import { PageLink } from '../router';

const LINES: ChartLine[] = [
  {
    key: 'instantThreat',
    label: 'Cumulative',
    color: '#ff8b5e',
    accessor: (s: Sample) => s.instantThreat,
  },
];

export function InstantThreatChart() {
  const { ruleset, views, comparing, tuning, playerCount, setPlayerCount } = useLab();
  const [scaleType, setScaleType] = useState<'linear' | 'log'>('linear');

  // While comparing, one branch having instant threat is reason enough to draw
  // the chart: the other's flat zero *is* the comparison. Only when nothing
  // selected has it does the row fall back to explaining itself.
  const anyInstantThreat = views.some((view) => view.ruleset.hasInstantThreat);
  const anyPlayerCount = views.some((view) => view.ruleset.usesPlayerCount);

  const yDomain = useCallback(
    (samples: Sample[]): [number, number] => {
      if (!samples.length) return [0, 1];
      const max = samples[samples.length - 1].instantThreat;
      if (scaleType === 'log') {
        const positive = samples.find((s) => s.instantThreat > 0);
        return [positive ? positive.instantThreat : 1, Math.max(max, 10)];
      }
      return [samples[0].instantThreat, max];
    },
    [scaleType],
  );

  // Nothing to plot when every selected branch returns zero for every send. A
  // flat line at zero on its own would be a chart that looks broken rather
  // than one that says so.
  if (!anyInstantThreat) {
    return (
      <ChartNoticeRow
        index={2}
        title="Instant threat"
        info={
          <>
            <p>
              <code>{ruleset.branch}</code> deletes instant threat: every send returns{' '}
              <code>threat_increase = 0</code>, and the player-count modifier goes with it.
            </p>
            <p>
              On live this is where most of a send's threat comes from. Here a send buys evolution
              and a permanently higher income and nothing lands immediately, which moves the game
              away from timing a send and towards the size of the stock behind it.
            </p>
          </>
        }
        infoMore={
          <PageLink page="threat" section="what">
            Understanding threat
          </PageLink>
        }
        // Tuning `c` to zero lands here. The control has to come along, or
        // nothing left on the page could put the curve back.
        params={tuning ? <InstantThreatParams /> : undefined}
      >
        <p>
          Removed on <code>{ruleset.branch}</code> — <code>threat_increase = 0</code>.
        </p>
        <p className="chart-notice-sub">
          Threat now arrives only as passive income, on the row below. The player-count modifier
          went with it.
        </p>
      </ChartNoticeRow>
    );
  }

  return (
    <ChartFrame
      index={2}
      title="Instant threat"
      params={tuning ? <InstantThreatParams /> : undefined}
      info={
        <>
          <p>
            Threat delivered by the send itself, accumulated from evolution 0. The rate depends only
            on where evolution currently is — so it is concave below 100% and grows{' '}
            <b>quadratically</b> above it, where the late game turns from an evolution race into a
            threat race.
          </p>
          <p>
            <b>Players</b> lives here because instant threat is the only thing it touches; nothing
            else in the scenario reads the connected-player count.
          </p>
          {comparing && !views.every((view) => view.ruleset.hasInstantThreat) ? (
            <p>A branch drawn flat along zero has no instant threat at all.</p>
          ) : null}
        </>
      }
      infoMore={
        <PageLink page="threat" section="dynamics">
          Why the curve bends
        </PageLink>
      }
      lines={LINES}
      yDomain={yDomain}
      yScaleType={scaleType}
      formatY={(value) => formatCompact(value, 1)}
      yLabel="threat"
      shadeSelection
      railExtra={(summary) =>
        // A point selection has no span to sum; its cumulative value is already
        // the line above.
        summary.mutagen > 0 ? (
          <div className="readout readout-accented">
            <span className="readout-label">Over selection</span>
            <b className="readout-value">{formatCompact(summary.instantThreat, 2)}</b>
          </div>
        ) : null
      }
      controls={
        <>
          <ScaleToggle value={scaleType} onChange={setScaleType} />
          {anyPlayerCount ? (
            <Field label="Players" inline>
              <NumberField
                value={playerCount}
                onChange={setPlayerCount}
                min={0}
                max={200}
                step={1}
              />
            </Field>
          ) : null}
        </>
      }
    />
  );
}
